import { useState, useEffect, useMemo } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { getProjectById, updateProject, addGardenerToProject, removeGardenerFromProject, getProjectDisplayName, type Project } from '@data/projects';
import { listProjectReplans, revertProjectReplan, type ProjectReplanVersion } from '@services/projects';
import { gardeners, type Gardener } from '@data/gardeners';
import { getPublicPlanById, type PublicPlan } from '@data/publicPlans';
import { getOffers, type Offer } from '@data/offers';
import { getMachinesByOrganization } from '@data/machines';
import { getGardeners, type UserResponse } from '@services/users';
import GanttChart from '@components/GanttChart';
import TaskEditModal from '@components/TaskEditModal';
import TaskCreateModal from '@components/TaskCreateModal';
import BackButton from '../../shared/BackButton';
import { useAuth } from '@services/auth';
import { useAdminViewAs, getMessagingActingUserId } from '../../lib/adminViewAs';
import type { GanttTask } from '../../types/gantt';
import { generateSyntheticGanttData } from '@utils/ganttDataGenerator';
import { syncGanttTaskToDatabase, syncProjectGanttTasksToDatabase, updateGanttTaskWithDatabaseId } from '@utils/syncTasksToDatabase';
import { getComponentLabel } from '@data/componentLabels';
import { getImageUrl } from '@utils/imageUrl';
import ImageModal from '../../shared/ImageModal';
import ConversationThread from '@components/ConversationThread';
import * as tasksAPI from '@services/tasks';
import type { TaskResponse } from '@services/tasks';
import { generateProjectPDF } from '@utils/pdfGenerator';
import { normalizePricingBreakdown } from '@utils/pricingBreakdownUtils';
import { useTranslatedAIContent } from '../../shared/useTranslatedAIContent';
import { useI18n } from '../../i18n';
import { getSkillLabel } from '@utils/skillLabels';
import { getMyCompany } from '@services/companies';

export default function ProjectDetail() {
	const { projectId } = useParams<{ projectId: string }>();
	const navigate = useNavigate();
	const [searchParams, setSearchParams] = useSearchParams();
	const { user } = useAuth();
	const { viewAsTarget } = useAdminViewAs();
	const [project, setProject] = useState<Project | undefined>(undefined);
	const [isLoading, setIsLoading] = useState(true);
	const [isDownloadingPdf, setIsDownloadingPdf] = useState(false);
	const tabFromUrl = searchParams.get('tab') as 'overview' | 'timeline' | 'team' | 'messages' | 'questionnaire' | null;
	const [activeTab, setActiveTab] = useState<'overview' | 'timeline' | 'team' | 'messages' | 'questionnaire'>('overview');

	// Sync tab from URL (e.g. notification deep link ?tab=messages)
	useEffect(() => {
		if (tabFromUrl && ['overview', 'timeline', 'team', 'messages', 'questionnaire'].includes(tabFromUrl)) {
			setActiveTab(tabFromUrl);
		}
	}, [tabFromUrl]);
	const [detailTab, setDetailTab] = useState<'conceptual' | 'detailed' | 'project-plan'>('conceptual');
	const [availableGardeners, setAvailableGardeners] = useState<UserResponse[]>([]);
	const [availableMachines, setAvailableMachines] = useState<any[]>([]);
	const [selectedGardenerId, setSelectedGardenerId] = useState<string>('');
	const [plan, setPlan] = useState<PublicPlan | undefined>(undefined);
	const { translatedContent: translatedPlanAi } = useTranslatedAIContent(plan?.aiGenerated ?? null);
	const planAi = translatedPlanAi ?? plan?.aiGenerated;
	const pricingBreakdown = useMemo(
		() => (planAi?.pricingBreakdown ? normalizePricingBreakdown(planAi.pricingBreakdown) : undefined),
		[planAi?.pricingBreakdown]
	);
	const [offer, setOffer] = useState<Offer | undefined>(undefined);
	const [dbTasks, setDbTasks] = useState<TaskResponse[]>([]);
	const [editingTask, setEditingTask] = useState<GanttTask | null>(null);
	const [isLoadingGardeners, setIsLoadingGardeners] = useState(false);
	const [showCreateTaskModal, setShowCreateTaskModal] = useState(false);
	const [imageModalOpen, setImageModalOpen] = useState(false);
	const [imageModalIndex, setImageModalIndex] = useState(0);
	const [replans, setReplans] = useState<ProjectReplanVersion[]>([]);
	const [isLoadingReplans, setIsLoadingReplans] = useState(false);
	const [isReverting, setIsReverting] = useState(false);
	const [isEditingMeta, setIsEditingMeta] = useState(false);
	const [metaDraftName, setMetaDraftName] = useState('');
	const [metaDraftClient, setMetaDraftClient] = useState('');
	const [isSavingMeta, setIsSavingMeta] = useState(false);
	const [companyCountryCode, setCompanyCountryCode] = useState<string | null>(null);
	const { language } = useI18n();
	const copy = (en: string, de: string, hr: string) =>
		language === 'de' ? de : language === 'hr' ? hr : en;
	const localizeProjectFocus = (focus?: string) => {
		switch (focus) {
			case 'planting_only':
				return copy('Planting only', 'Nur Bepflanzung', 'Samo sadnja');
			case 'construction_hardscape_only':
				return copy('Construction / hardscape only', 'Nur Bau / Hardscape', 'Samo gradnja / hardscape');
			case 'mixed_planting_construction':
				return copy('Mixed (planting + construction)', 'Gemischt (Bepflanzung + Bau)', 'Mješovito (sadnja + gradnja)');
			default:
				return focus || '-';
		}
	};

	useEffect(() => {
		let cancelled = false;
		if (user?.role !== 'company' && user?.role !== 'gardener') {
			setCompanyCountryCode(null);
			return;
		}
		if (!user?.id) return;
		getMyCompany(String(user.id), user.activeCompanyId ?? undefined)
			.then((company) => {
				if (!cancelled) setCompanyCountryCode(company?.company?.country_code || null);
			})
			.catch(() => {
				if (!cancelled) setCompanyCountryCode(null);
			});
		return () => {
			cancelled = true;
		};
	}, [user?.role, user?.activeCompanyId]);

	useEffect(() => {
		if (!projectId) return;

		const loadProject = async () => {
			setIsLoading(true);
			try {
				const loadedProject = await getProjectById(projectId);
				if (loadedProject) {
					setProject(loadedProject);
					
					// Load available gardeners from API
					setIsLoadingGardeners(true);
					try {
						const apiGardeners = await getGardeners();
						
						// Check if it's an array and has items
						if (!Array.isArray(apiGardeners)) {
							console.error('API did not return an array:', typeof apiGardeners, apiGardeners);
							throw new Error('API returned non-array response');
						}
						
						// Filter out any invalid entries and ensure we have valid data
						// Backend now guarantees name via COALESCE, so we only need to check id
						const validGardeners = apiGardeners.filter(g => {
							const isValid = g && g.id && (g.name || g.company_name || g.email);
							if (!isValid) {
								console.warn('Filtered out invalid gardener:', g);
							}
							return isValid;
						});
						
						if (validGardeners.length > 0) {
							setAvailableGardeners(validGardeners);
						} else {
							console.warn('No valid gardeners returned from API. API returned:', apiGardeners);
							console.warn('This might mean: 1) No users with gardener/company role in database, 2) API query issue, 3) Data format issue');
							// Fallback to mock data if API returns empty or invalid data
							setAvailableGardeners(gardeners.map(g => ({
								id: parseInt(g.id.replace('gard', '')) || 0,
								email: '',
								name: g.name,
								role: g.role === 'lead' ? 'gardener' : g.role === 'specialist' ? 'gardener' : 'gardener',
								created_at: '',
								updated_at: ''
							})));
						}
					} catch (err) {
						console.error('Failed to load gardeners from API:', err);
						console.error('Error details:', err instanceof Error ? err.message : err);
						// Fallback to mock data if API fails
						setAvailableGardeners(gardeners.map(g => ({
							id: parseInt(g.id.replace('gard', '')) || 0,
							email: '',
							name: g.name,
							role: g.role === 'lead' ? 'gardener' : g.role === 'specialist' ? 'gardener' : 'gardener',
							created_at: '',
							updated_at: ''
						})));
					} finally {
						setIsLoadingGardeners(false);
					}
					
					// Load available machines (filter by organization if user has one)
					const orgId = user?.organizationId || 'org1';
					const machines = getMachinesByOrganization(orgId);
					setAvailableMachines(machines);
					
					// Load related plan and offer if available
					if (loadedProject.planId) {
						try {
							// Try loading from API first (if it's a database ID)
							const planIdNum = parseInt(loadedProject.planId, 10);
							if (!isNaN(planIdNum)) {
								// It's a database ID, try API
								const { getPublicPlanByIdFromAPI } = await import('@data/publicPlans');
								const apiPlan = await getPublicPlanByIdFromAPI(planIdNum);
								if (apiPlan) {
									setPlan(apiPlan);
								} else {
									console.warn('Plan not found in API, trying localStorage');
									// Fallback to localStorage - try both the numeric ID and db-{id} format
									const localPlan = getPublicPlanById(loadedProject.planId) || getPublicPlanById(`db-${planIdNum}`);
									if (localPlan) {
										setPlan(localPlan);
									} else {
										console.warn('Plan not found in localStorage either. Plan ID:', loadedProject.planId);
									}
								}
							} else if (loadedProject.planId.startsWith('db-')) {
								// It's a db-{id} format, extract the ID and try API
								const dbId = parseInt(loadedProject.planId.replace('db-', ''), 10);
								if (!isNaN(dbId)) {
									const { getPublicPlanByIdFromAPI } = await import('@data/publicPlans');
									const apiPlan = await getPublicPlanByIdFromAPI(dbId);
									if (apiPlan) {
										setPlan(apiPlan);
									} else {
										const localPlan = getPublicPlanById(loadedProject.planId);
										if (localPlan) setPlan(localPlan);
									}
								}
							} else {
								// It's a string ID, try localStorage
								const localPlan = getPublicPlanById(loadedProject.planId);
								if (localPlan) {
									setPlan(localPlan);
								}
							}
						} catch (err) {
							console.error('Failed to load plan:', err);
						}
					}
					
					if (loadedProject.offerId) {
						try {
							const relatedOffer = getOffers().find(o => o.id === loadedProject.offerId);
							if (relatedOffer) {
								setOffer(relatedOffer);
								// If project has no gantt data but offer does, use offer's gantt data
								if ((!loadedProject.ganttData || loadedProject.ganttData.length === 0) && relatedOffer.ganttData && relatedOffer.ganttData.length > 0) {
									await updateProject(loadedProject.id, { ganttData: relatedOffer.ganttData });
									// Reload project to get updated gantt data
									const updatedProject = await getProjectById(projectId);
									if (updatedProject) {
										setProject(updatedProject);
									}
								}
							}
						} catch (err) {
							console.warn('Failed to load offer:', err);
						}
					}
					
					// Generate mock timeline if no gantt data exists
					if ((!loadedProject.ganttData || loadedProject.ganttData.length === 0) && !offer?.ganttData) {
						// Determine start date: use offer's projectStartDate, plan's projectStartDate, or today
						let startDate = new Date();
						if (offer?.projectStartDate) {
							startDate = new Date(offer.projectStartDate);
						} else if (plan?.questionnaire?.projectStartDate) {
							startDate = new Date(plan.questionnaire.projectStartDate);
						}
						
						// Generate mock timeline
						const mockGanttData = generateSyntheticGanttData('garden', loadedProject.budget || 1500, startDate, user?.organizationId);
						
						// Sync all tasks to database first, then update their IDs
						if (projectId && mockGanttData.length > 0) {
							try {
								// Sync each task and update its ID with database ID
								const syncedTasks = await Promise.all(
									mockGanttData.map(async (task) => {
										const databaseId = await syncGanttTaskToDatabase(task, projectId);
										if (databaseId) {
											return updateGanttTaskWithDatabaseId(task, databaseId);
										}
										return task;
									})
								);
								
								// Save timeline with updated task IDs
								await updateProject(loadedProject.id, { ganttData: syncedTasks });
							} catch (err) {
								console.error('Failed to sync generated tasks to database:', err);
								// Still save the timeline even if sync fails
								await updateProject(loadedProject.id, { ganttData: mockGanttData });
							}
						} else {
							// Save generated timeline to project
							await updateProject(loadedProject.id, { ganttData: mockGanttData });
						}
						
						// Reload project to get updated gantt data
						const updatedProject = await getProjectById(projectId);
						if (updatedProject) {
							setProject(updatedProject);
						}
					}
				}
			} catch (err) {
				console.error('Failed to load project:', err);
			} finally {
				setIsLoading(false);
			}
		};

		loadProject();
	}, [projectId, user]);

	useEffect(() => {
		const handleProjectsUpdate = async () => {
			if (projectId) {
				const updated = await getProjectById(projectId);
				if (updated) {
					setProject(updated);
				}
			}
		};
		window.addEventListener('projectsUpdated', handleProjectsUpdate);
		return () => {
			window.removeEventListener('projectsUpdated', handleProjectsUpdate);
		};
	}, [projectId]);

	// Load backend tasks for this project so gardener timeline matches Tasks/Schedule/Calendar
	useEffect(() => {
		if (!project?.id) {
			setDbTasks([]);
			return;
		}
		const projectIdNum =
			typeof project.id === 'string' ? parseInt(project.id, 10) : (project.id as number | undefined);
		if (!projectIdNum || Number.isNaN(projectIdNum)) {
			setDbTasks([]);
			return;
		}
		let cancelled = false;
		tasksAPI
			.getTasksForProject(projectIdNum)
			.then(tasks => {
				if (!cancelled) setDbTasks(tasks || []);
			})
			.catch(() => {
				if (!cancelled) setDbTasks([]);
			});
		return () => {
			cancelled = true;
		};
	}, [project?.id]);

	useEffect(() => {
		if (!projectId) return;
		const loadReplans = async () => {
			setIsLoadingReplans(true);
			try {
				const data = await listProjectReplans(parseInt(projectId, 10));
				setReplans(data);
			} catch (err) {
				console.error('Failed to load replans:', err);
				setReplans([]);
			} finally {
				setIsLoadingReplans(false);
			}
		};
		loadReplans();
	}, [projectId]);

	const timelineGanttTasks = useMemo((): GanttTask[] => {
		if (!project) return [];
		if (dbTasks.length > 0) {
			return dbTasks.map((t) => ({
				id: `db-${t.id}`,
				name: t.title,
				description: t.description || undefined,
				type: t.type,
				startDate: t.start_date ? new Date(t.start_date).toISOString() : new Date().toISOString(),
				endDate: t.end_date ? new Date(t.end_date).toISOString() : new Date().toISOString(),
				duration: t.duration || 1,
				progress: t.progress || 0,
				status: t.status || 'not_started',
				dependencies: t.dependencies || [],
				isCritical: t.is_critical || false,
				gardenerIds: t.gardener_ids.map((id) => String(id)),
				// Backend provides human-readable names; include them so PDFs can show "Names (IDs)".
				gardenerNames: t.gardener_names || [],
				machineIds: t.machine_ids || [],
				projectId: project.id,
				notes: t.notes || undefined,
				plant_id: t.plant_id,
				plant_name: t.plant_name
			}));
		}
		return project.ganttData ?? [];
	}, [project, dbTasks]);

	const handleRevertReplan = async (replanId: number) => {
		if (!user?.id) return;
		setIsReverting(true);
		try {
			const userId = typeof user.id === 'string' ? parseInt(user.id, 10) : user.id;
			await revertProjectReplan(replanId, userId);
			const updated = await getProjectById(projectId!);
			if (updated) setProject(updated);
			const data = await listProjectReplans(parseInt(projectId!, 10));
			setReplans(data);
		} catch (err) {
			console.error('Failed to revert replan:', err);
			alert('Failed to revert. Please try again.');
		} finally {
			setIsReverting(false);
		}
	};

	if (!project) {
		return (
			<div className="container-fluid py-4">
				<BackButton fallbackPath="/app/projects" label="Back to Projects" />
				<div className="alert alert-warning">Project not found.</div>
			</div>
		);
	}

	const assignedGardeners = project.gardenerIds
		? availableGardeners.filter(g => {
				const gardenerIdStr = String(g.id);
				return project.gardenerIds!.some(id => String(id) === gardenerIdStr);
			})
		: [];

	const normalizeCompanyName = (value?: string | null) => (value || '').trim().toLowerCase();
	const targetCompanyId =
		(project.companyId && !Number.isNaN(parseInt(project.companyId, 10)))
			? parseInt(project.companyId, 10)
			: (user?.activeCompanyId ?? user?.companyId ?? null);
	const targetCompanyName = normalizeCompanyName(project.companyName || user?.companyName);

	const companyScopedGardeners = availableGardeners.filter((g) => {
		const gardenerCompanyId = g.active_company_id ?? g.company_id ?? null;
		if (targetCompanyId != null && gardenerCompanyId != null) {
			return Number(gardenerCompanyId) === Number(targetCompanyId);
		}
		if (targetCompanyName) {
			return normalizeCompanyName(g.company_name) === targetCompanyName;
		}
		return true;
	});

	const handleAddGardener = async () => {
		if (selectedGardenerId && projectId) {
			try {
				const isAllowedByCompany = companyScopedGardeners.some(g => String(g.id) === selectedGardenerId);
				if (!isAllowedByCompany) {
					window.alert(copy(
						'You can only add team members from this company.',
						'Sie können nur Teammitglieder aus diesem Unternehmen hinzufügen.',
						'Možete dodati samo članove tima iz ove tvrtke.'
					));
					return;
				}
				await addGardenerToProject(projectId, selectedGardenerId);
				setSelectedGardenerId('');
				// Reload project to get updated gardener list
				const updatedProject = await getProjectById(projectId);
				if (updatedProject) {
					setProject(updatedProject);
				}
			} catch (err) {
				console.error('Failed to add gardener:', err);
			}
		}
	};

	const handleRemoveGardener = async (gardenerId: string) => {
		if (projectId) {
			try {
				await removeGardenerFromProject(projectId, gardenerId);
				// Reload project to get updated gardener list
				const updatedProject = await getProjectById(projectId);
				if (updatedProject) {
					setProject(updatedProject);
				}
			} catch (err) {
				console.error('Failed to remove gardener:', err);
			}
		}
	};

	const handleStatusChange = (newStatus: Project['status']) => {
		if (projectId) {
			updateProject(projectId, { status: newStatus });
		}
	};

	const startEditingMeta = () => {
		if (!project) return;
		setMetaDraftName(project.name || '');
		setMetaDraftClient(project.client || '');
		setIsEditingMeta(true);
	};

	const cancelEditingMeta = () => {
		setIsEditingMeta(false);
	};

	const saveMeta = async () => {
		if (!project || !projectId) return;
		const trimmedName = metaDraftName.trim();
		const trimmedClient = metaDraftClient.trim();
		if (!trimmedName && !trimmedClient) {
			return;
		}
		setIsSavingMeta(true);
		try {
			await updateProject(project.id, {
				name: trimmedName || 'Untitled project',
				client: trimmedClient || project.client || ''
			});
			const updated = await getProjectById(projectId);
			if (updated) {
				setProject(updated);
			}
			setIsEditingMeta(false);
		} catch (err) {
			console.error('Failed to update project meta:', err);
		} finally {
			setIsSavingMeta(false);
		}
	};
	
	const handleTaskClick = (task: GanttTask) => {
		setEditingTask(task);
	};

	/** Persist timeline edits when tasks are loaded from the API (ids like db-123). */
	const handleTaskUpdate = async (taskId: string, updates: Partial<GanttTask>) => {
		if (!project || !projectId) return;

		const parseDbTaskId = (id: string): number | null => {
			if (!id.startsWith('db-')) return null;
			const n = parseInt(id.replace(/^db-/, ''), 10);
			return Number.isFinite(n) ? n : null;
		};

		const dbTaskId = parseDbTaskId(taskId);
		const projectIdNum = parseInt(projectId, 10);

		if (dbTaskId !== null && !Number.isNaN(projectIdNum)) {
			try {
				const payload: tasksAPI.UpdateTaskPayload = {};
				if (updates.startDate != null) payload.start_date = updates.startDate.split('T')[0];
				if (updates.endDate != null) {
					const day = updates.endDate.split('T')[0];
					payload.end_date = day;
					payload.due_date = day;
				}
				if (updates.duration != null) payload.duration = updates.duration;
				if (updates.progress != null) payload.progress = updates.progress;
				if (updates.status != null) payload.status = updates.status;
				if (updates.dependencies != null) payload.dependencies = updates.dependencies;
				if (updates.isCritical != null) payload.is_critical = updates.isCritical;
				if (updates.name != null) payload.title = updates.name;
				if (updates.description != null) payload.description = updates.description;
				if (updates.notes != null) payload.notes = updates.notes;
				if (updates.type != null) payload.type = updates.type;
				if (updates.plant_id != null) payload.plant_id = updates.plant_id;
				if (updates.plant_name != null) payload.plant_name = updates.plant_name;
				if (updates.requiredSkills != null) payload.required_skills = updates.requiredSkills;
				if (updates.requiredGardeners != null) payload.required_gardeners = updates.requiredGardeners;

				if (Object.keys(payload).length > 0) {
					await tasksAPI.updateTask(dbTaskId, payload);
				}

				const existingRow = dbTasks.find((t) => t.id === dbTaskId);

				if (updates.gardenerIds !== undefined && existingRow) {
					const prev = new Set(existingRow.gardener_ids.map((g) => String(g)));
					const next = new Set((updates.gardenerIds || []).map(String));
					for (const gid of next) {
						if (!prev.has(gid)) {
							const n = parseInt(gid, 10);
							if (!Number.isNaN(n)) await tasksAPI.assignGardenerToTask(dbTaskId, n);
						}
					}
					for (const gid of prev) {
						if (!next.has(gid)) {
							const n = parseInt(gid, 10);
							if (!Number.isNaN(n)) await tasksAPI.removeGardenerFromTask(dbTaskId, n);
						}
					}
				}

				if (updates.machineIds !== undefined && existingRow) {
					const prev = new Set((existingRow.machine_ids || []).map(String));
					const next = new Set((updates.machineIds || []).map(String));
					for (const mid of next) {
						if (!prev.has(mid)) {
							const m = availableMachines.find((x: { id?: string }) => String(x.id) === mid);
							await tasksAPI.assignMachineToTask(dbTaskId, mid, m?.name);
						}
					}
					for (const mid of prev) {
						if (!next.has(mid)) {
							await tasksAPI.removeMachineFromTask(dbTaskId, mid);
						}
					}
				}

				const refreshedTasks = await tasksAPI.getTasksForProject(projectIdNum);
				setDbTasks(refreshedTasks || []);

				const updatedProject = await getProjectById(projectId);
				if (updatedProject) setProject(updatedProject);
			} catch (err) {
				console.error('Failed to update task:', err);
				alert('Failed to save task changes. Please try again.');
			}
			setEditingTask(null);
			return;
		}

		// Legacy: tasks stored only in project.ganttData (no db- id)
		if (!project.ganttData) return;

		const updatedTasks = project.ganttData.map((t) => (t.id === taskId ? { ...t, ...updates } : t));

		const updatedTask = updatedTasks.find((t) => t.id === taskId);
		if (updatedTask) {
			try {
				const databaseId = await syncGanttTaskToDatabase(updatedTask, projectId);
				if (databaseId) {
					const taskIndex = updatedTasks.findIndex((t) => t.id === taskId);
					if (taskIndex !== -1) {
						updatedTasks[taskIndex] = updateGanttTaskWithDatabaseId(updatedTasks[taskIndex], databaseId);
					}
				}
			} catch (err) {
				console.error('Failed to sync task to database:', err);
			}
		}

		await updateProject(project.id, { ganttData: updatedTasks });
		const updatedProject = await getProjectById(projectId);
		if (updatedProject) {
			setProject(updatedProject);
		}
		setEditingTask(null);
	};
	
	const handleGenerateTimeline = async () => {
		if (!project) return;
		
		// Determine start date: use offer's projectStartDate, plan's projectStartDate, or today
		let startDate = new Date();
		if (offer?.projectStartDate) {
			startDate = new Date(offer.projectStartDate);
		} else if (plan?.questionnaire?.projectStartDate) {
			startDate = new Date(plan.questionnaire.projectStartDate);
		}
		
		// Generate mock timeline
		const mockGanttData = generateSyntheticGanttData('garden', project.budget || 1500, startDate, user?.organizationId);
		
		// Sync all tasks to database first, then update their IDs
		if (projectId && mockGanttData.length > 0) {
			try {
				// Sync each task and update its ID with database ID
				const syncedTasks = await Promise.all(
					mockGanttData.map(async (task) => {
						const databaseId = await syncGanttTaskToDatabase(task, projectId);
						if (databaseId) {
							return updateGanttTaskWithDatabaseId(task, databaseId);
						}
						return task;
					})
				);
				
				// Save timeline with updated task IDs
				await updateProject(project.id, { ganttData: syncedTasks });
			} catch (err) {
				console.error('Failed to sync generated tasks to database:', err);
				// Still save the timeline even if sync fails
				await updateProject(project.id, { ganttData: mockGanttData });
			}
		} else {
			// Save generated timeline to project
			await updateProject(project.id, { ganttData: mockGanttData });
		}
		
		// Reload project to get updated gantt data
		const updatedProject = await getProjectById(projectId!);
		if (updatedProject) {
			setProject(updatedProject);
		}
	};

	const getTabStyle = (tab: 'overview' | 'timeline' | 'team' | 'messages' | 'questionnaire') => ({
		color: activeTab === tab ? '#198754' : '#6c757d',
		borderBottom: activeTab === tab ? '3px solid #198754' : '3px solid transparent',
		fontWeight: activeTab === tab ? 600 : 400,
		backgroundColor: 'transparent',
		borderTop: 'none',
		borderLeft: 'none',
		borderRight: 'none',
		transition: 'all 0.2s ease'
	});

	const getDetailTabStyle = (tab: 'conceptual' | 'detailed' | 'project-plan') => ({
		color: detailTab === tab ? '#198754' : '#6c757d',
		borderBottom: detailTab === tab ? '3px solid #198754' : '3px solid transparent',
		fontWeight: detailTab === tab ? 600 : 400,
		backgroundColor: 'transparent',
		borderTop: 'none',
		borderLeft: 'none',
		borderRight: 'none',
		transition: 'all 0.2s ease'
	});

	if (isLoading) {
		return (
			<div className="container-fluid py-4">
				<BackButton fallbackPath="/app/projects" label="Back to Projects" />
				<div className="text-center py-5">
					<div className="spinner-border text-primary" role="status">
						<span className="visually-hidden">Loading project...</span>
					</div>
				</div>
			</div>
		);
	}

	if (!project) {
		return (
			<div className="container-fluid py-4">
				<BackButton fallbackPath="/app/projects" label="Back to Projects" />
				<div className="alert alert-warning">
					Project not found.
				</div>
			</div>
		);
	}

	async function handleDownloadProjectPDF() {
		if (isDownloadingPdf) return;
		if (!project) {
			window.alert('Project not found.');
			return;
		}
		const vision = (offer?.planSnapshot?.aiGenerated ?? plan?.aiGenerated) as any;
		// Use backend-synced tasks so the PDF matches the same timeline as the UI.
		const tasks = timelineGanttTasks;
		if (!vision || tasks.length === 0) {
			window.alert('Project PDF is not available yet (missing plan vision or timeline tasks).');
			return;
		}

		try {
			setIsDownloadingPdf(true);
			const offerMeta = offer
				? {
					bands: offer.bands,
					message: offer.message,
					validUntil: offer.validUntil,
					projectStartDate: offer.projectStartDate,
					timelineEstimate: offer.timelineEstimate
				}
				: undefined;

			await generateProjectPDF({
				projectTitle: getProjectDisplayName(project),
				projectStatusLabel: project.status ? project.status.replace('_', ' ') : 'Unknown',
				gardenVision: vision,
				ganttTasks: tasks,
				offerMeta,
				client: project.client,
				companyName: project.gardenerName,
				budget: project.budget,
				dueDate: project.dueDate,
				beforeImageUrl: (plan?.photoBeforeUrl || plan?.questionnaire?.photoBeforeUrl) ?? null,
				afterImageUrl: vision.photoAfterUrl || null,
				detailLevel: 'company',
				language
			});
		} catch (err) {
			console.error('Failed to generate project PDF:', err);
			window.alert('Failed to generate project PDF. Please try again.');
		} finally {
			setIsDownloadingPdf(false);
		}
	}

	return (
		<div className="container-fluid py-4 px-3 px-md-4">
			<BackButton fallbackPath="/app/projects" label={copy('Back to Projects', 'Zurück zu Projekten', 'Natrag na projekte')} />
			
			{/* Project header */}
			<div className="d-flex flex-column flex-md-row justify-content-between align-items-start gap-3 mb-4 pb-3 border-bottom border-2 border-success border-opacity-25">
				<div className="flex-grow-1">
					{isEditingMeta ? (
						<div>
							<h1 className="h3 mb-2 fw-bold text-dark">
								<input
									type="text"
									className="form-control form-control-sm"
									value={metaDraftName}
									onChange={(e) => setMetaDraftName(e.target.value)}
									placeholder={copy('Project name (e.g. Backyard, Front garden)', 'Projektname (z. B. Hinterhof, Vorgarten)', 'Naziv projekta (npr. stražnje dvorište, prednji vrt)')}
								/>
							</h1>
							<div className="mb-2">
								<label className="text-muted small mb-1">{copy('Client / seeker', 'Kunde / Suchender', 'Klijent / vlasnik')}</label>
								<input
									type="text"
									className="form-control form-control-sm"
									value={metaDraftClient}
									onChange={(e) => setMetaDraftClient(e.target.value)}
									placeholder={copy('Client name or email', 'Name oder E-Mail des Kunden', 'Ime ili e-pošta klijenta')}
								/>
							</div>
							{project.gardenerName && (
								<p className="text-muted small mb-0">{copy('Company:', 'Firma:', 'Tvrtka:')} {project.gardenerName}</p>
							)}
						</div>
					) : (
						<div>
							<h1 className="h3 mb-1 fw-bold text-dark">
								{getProjectDisplayName(project)}
							</h1>
							<p className="text-muted mb-0 small">{copy('Client:', 'Kunde:', 'Klijent:')} {project.client || '-'}</p>
							{project.gardenerName && (
								<p className="text-muted small mb-0">{copy('Company:', 'Firma:', 'Tvrtka:')} {project.gardenerName}</p>
							)}
						</div>
					)}
				</div>
				<div className="d-flex gap-2 align-items-center flex-wrap">
					{isEditingMeta ? (
						<>
							<button
								type="button"
								className="btn btn-sm btn-success"
								onClick={saveMeta}
								disabled={isSavingMeta}
							>
								{isSavingMeta ? copy('Saving...', 'Speichern...', 'Spremanje...') : copy('Save', 'Speichern', 'Spremi')}
							</button>
							<button
								type="button"
								className="btn btn-sm btn-outline-secondary"
								onClick={cancelEditingMeta}
								disabled={isSavingMeta}
							>
								{copy('Cancel', 'Abbrechen', 'Odustani')}
							</button>
						</>
					) : (
						<button
							type="button"
							className="btn btn-sm btn-outline-success me-2"
							onClick={startEditingMeta}
						>
							<i className="bi bi-pencil-square me-1" aria-hidden></i>
							{copy('Edit name', 'Name bearbeiten', 'Uredi naziv')}
						</button>
					)}
					<select
						className="form-select form-select-sm border-success"
						value={project.status}
						onChange={(e) => handleStatusChange(e.target.value as Project['status'])}
						style={{ width: 'auto', minWidth: 140 }}
					>
						<option value="planning">{copy('Planning', 'Planung', 'Planiranje')}</option>
						<option value="quoted">{copy('Quoted', 'Angeboten', 'Ponuda poslana')}</option>
						<option value="in_progress">{copy('In Progress', 'In Bearbeitung', 'U tijeku')}</option>
						<option value="completed">{copy('Completed', 'Abgeschlossen', 'Završeno')}</option>
					</select>
					<button
						type="button"
						className="btn btn-sm btn-outline-success"
						onClick={handleDownloadProjectPDF}
						disabled={isDownloadingPdf || !((offer?.planSnapshot?.aiGenerated ?? plan?.aiGenerated) as any) || !(project.ganttData && project.ganttData.length > 0)}
						title={!((offer?.planSnapshot?.aiGenerated ?? plan?.aiGenerated) as any) ? copy('Project PDF not available yet', 'Projekt-PDF ist noch nicht verfügbar', 'Projektni PDF još nije dostupan') : copy('Download project PDF', 'Projekt-PDF herunterladen', 'Preuzmi projektni PDF')}
					>
						<i className="bi bi-download me-2"></i>
						{copy('Download PDF', 'PDF herunterladen', 'Preuzmi PDF')}
					</button>
				</div>
			</div>

			{/* Tabs */}
			<ul className="nav nav-tabs mb-4 gap-2">
				<li className="nav-item">
					<button
						className="nav-link"
						style={getTabStyle('overview')}
						onClick={() => { setActiveTab('overview'); setSearchParams({}); }}
					>
						{copy('Overview', 'Übersicht', 'Pregled')}
					</button>
				</li>
				<li className="nav-item">
					<button
						className="nav-link"
						style={getTabStyle('timeline')}
						onClick={() => { setActiveTab('timeline'); setSearchParams({}); }}
					>
						{copy('Timeline', 'Zeitplan', 'Vremenski plan')}
					</button>
				</li>
				<li className="nav-item">
					<button
						className="nav-link"
						style={getTabStyle('team')}
						onClick={() => { setActiveTab('team'); setSearchParams({}); }}
					>
						{copy('Team', 'Team', 'Tim')} ({assignedGardeners.length})
					</button>
				</li>
				<li className="nav-item">
					<button
						className="nav-link"
						style={getTabStyle('messages')}
						onClick={() => { setActiveTab('messages'); setSearchParams({ tab: 'messages' }); }}
					>
						{copy('Messages', 'Nachrichten', 'Poruke')}
					</button>
				</li>
				<li className="nav-item">
					<button
						className="nav-link"
						style={getTabStyle('questionnaire')}
						onClick={() => { setActiveTab('questionnaire'); setSearchParams({ tab: 'questionnaire' }); }}
					>
						{copy('Client Inputs', 'Kundeneingaben', 'Unos klijenta')}
					</button>
				</li>
			</ul>

			{/* Tab Content */}
			{activeTab === 'overview' && (
				<div className="row g-4">
					<div className="col-12 col-md-6">
						<div className="card shadow-sm">
							<div className="card-body">
								<h5 className="card-title text-success">{copy('Project Details', 'Projektdetails', 'Detalji projekta')}</h5>
								<dl className="row mb-0">
									<dt className="col-sm-4">{copy('Status', 'Status', 'Status')}</dt>
									<dd className="col-sm-8">
										<span className={`badge ${
											project.status === 'completed' ? 'text-bg-success' :
											project.status === 'in_progress' ? 'text-bg-primary' :
											project.status === 'quoted' ? 'text-bg-info' :
											'text-bg-secondary'
										}`}>
											{project.status ? project.status.replace('_', ' ') : copy('Unknown', 'Unbekannt', 'Nepoznato')}
										</span>
									</dd>
									<dt className="col-sm-4">{copy('Budget', 'Budget', 'Budžet')}</dt>
									<dd className="col-sm-8">{project.budget ? project.budget.toLocaleString() : '0'} €</dd>
									<dt className="col-sm-4">{copy('Priority', 'Priorität', 'Prioritet')}</dt>
									<dd className="col-sm-8">
										<span className={`badge ${
											project.priority === 'high' ? 'text-bg-danger' :
											project.priority === 'medium' ? 'text-bg-warning' :
											'text-bg-info'
										}`}>
											{project.priority || 'medium'}
										</span>
									</dd>
									{project.dueDate && (
										<>
											<dt className="col-sm-4">{copy('Due Date', 'Fälligkeitsdatum', 'Datum dospijeća')}</dt>
											<dd className="col-sm-8">
												{new Date(project.dueDate).toLocaleDateString('en-GB')}
											</dd>
										</>
									)}
									<dt className="col-sm-4">{copy('Owner', 'Verantwortlich', 'Vlasnik')}</dt>
									<dd className="col-sm-8">{project.owner || project.gardenerName || '-'}</dd>
								</dl>
							</div>
						</div>
					</div>
					<div className="col-12 col-md-6">
						<div className="card shadow-sm">
							<div className="card-body">
								<h5 className="card-title text-success">{copy('Tags', 'Schlagwörter', 'Oznake')}</h5>
								<div className="d-flex flex-wrap gap-2">
									{project.tags.map((tag) => (
										<span key={tag} className="badge text-bg-light text-muted">
											{tag}
										</span>
									))}
								</div>
								{project.offerId && (
									<div className="mt-3">
										<small className="text-muted">{copy('Created from accepted offer', 'Aus angenommenem Angebot erstellt', 'Iz prihvaćene ponude')}</small>
									</div>
								)}
							</div>
						</div>
					</div>

					{/* Tasks progress summary - prefer DB tasks when available */}
					<div className="col-12">
						<div className="card h-100 shadow-sm">
							<div className="card-body">
								<div className="d-flex justify-content-between align-items-center mb-2">
									<h5 className="card-title mb-0 text-success">{copy('Tasks', 'Zadaci', 'Zadaci')}</h5>
									<button
										type="button"
										className="btn btn-sm btn-outline-success"
										onClick={() => setActiveTab('timeline')}
									>
										{copy('View timeline', 'Zeitplan anzeigen', 'Prikaži vremenski plan')}
									</button>
								</div>
								{(dbTasks.length > 0 || (project.ganttData && project.ganttData.length > 0)) ? (
									<>
										{(() => {
											const tasks: GanttTask[] =
												dbTasks.length > 0
													? dbTasks.map(t => ({
															id: `db-${t.id}`,
															name: t.title,
															description: t.description || undefined,
															startDate: t.start_date ? new Date(t.start_date).toISOString() : new Date().toISOString(),
															endDate: t.end_date ? new Date(t.end_date).toISOString() : new Date().toISOString(),
															duration: t.duration || 1,
															progress: t.progress || 0,
															status: t.status || 'not_started',
															dependencies: t.dependencies || [],
															isCritical: t.is_critical || false,
															gardenerIds: t.gardener_ids.map(id => String(id)),
															machineIds: t.machine_ids || [],
															projectId: project.id,
															notes: t.notes || undefined
													  }))
													: (project.ganttData ?? []);
											const completed = tasks.filter(t => (t.progress ?? 0) >= 100 || t.status === 'completed').length;
											const inProgress = tasks.filter(t => t.status === 'in_progress' || ((t.progress ?? 0) > 0 && (t.progress ?? 0) < 100)).length;
											const total = tasks.length;
											const pct = total ? Math.round((completed / total) * 100) : 0;
											return (
												<>
													<div className="d-flex align-items-center gap-3 mb-2">
														<div className="flex-grow-1" style={{ minWidth: 0 }}>
															<div className="progress" style={{ height: 8 }}>
																<div
																	className="progress-bar bg-success"
																	role="progressbar"
																	style={{ width: `${pct}%` }}
																	aria-valuenow={pct}
																	aria-valuemin={0}
																	aria-valuemax={100}
																/>
															</div>
														</div>
														<span className="small text-muted text-nowrap">{completed}/{total} {copy('completed', 'abgeschlossen', 'završeno')}</span>
													</div>
													{inProgress > 0 && (
														<p className="small text-muted mb-2 mb-md-0">{inProgress} {copy('in progress', 'u tijeku', 'u tijeku')}</p>
													)}
													<ul className="list-unstyled mb-0 small">
														{tasks
															.slice(0, 5)
															.map((t) => (
																<li key={t.id} className="d-flex align-items-center gap-2 py-1">
																	<span className={`badge ${(t.progress ?? 0) >= 100 || t.status === 'completed' ? 'bg-success' : t.status === 'in_progress' ? 'bg-primary' : 'bg-secondary'} text-nowrap`} style={{ width: 28, fontSize: 10 }}>
																		{(t.progress ?? 0) >= 100 || t.status === 'completed' ? '✓' : (t.progress ?? 0) > 0 ? '…' : '○'}
																	</span>
																	<span className="text-truncate">{t.name}</span>
																</li>
															))}
													</ul>
													{tasks.length > 5 && (
														<button type="button" className="btn btn-link btn-sm p-0 mt-1" onClick={() => setActiveTab('timeline')}>
															+{tasks.length - 5} {copy('more on timeline', 'mehr im Zeitplan', 'više na vremenskom planu')}
														</button>
													)}
												</>
											);
										})()}
									</>
								) : (
									<p className="text-muted small mb-2">{copy('No tasks yet.', 'Noch keine Aufgaben.', 'Još nema zadataka.')}</p>
								)}
								{(user?.role === 'company' || user?.role === 'gardener') && (!project.ganttData || project.ganttData.length === 0) && (
									<button type="button" className="btn btn-success btn-sm" onClick={handleGenerateTimeline}>
										{copy('Generate timeline', 'Generiraj vremenski plan', 'Generiraj vremenski plan')}
									</button>
								)}
							</div>
						</div>
					</div>

					{/* Project Details Tabs */}
					<div className="col-12">
						<div className="card shadow-sm">
							<div className="card-body">
								{/* Detail Tabs Navigation */}
								<ul className="nav nav-tabs mb-3">
									<li className="nav-item">
										<button
											className="nav-link"
											style={getDetailTabStyle('conceptual')}
											onClick={() => setDetailTab('conceptual')}
										>
											{copy('Conceptual', 'Konceptualno', 'Konceptualno')}
										</button>
									</li>
									<li className="nav-item">
										<button
											className="nav-link"
											style={getDetailTabStyle('detailed')}
											onClick={() => setDetailTab('detailed')}
										>
											{copy('Project Plan', 'Plan projekta', 'Plan projekta')}
										</button>
									</li>
									<li className="nav-item">
										<button
											className="nav-link"
											style={getDetailTabStyle('project-plan')}
											onClick={() => setDetailTab('project-plan')}
										>
											{copy('Detailed', 'Detaljno', 'Detaljno')}
										</button>
									</li>
								</ul>

								{/* Conceptual Tab Content */}
								{detailTab === 'conceptual' && (
									<div>
										{plan?.aiGenerated ? (
											<div>
												{planAi?.planDescription && (
													<div className="mb-3">
														<h6 className="fw-semibold mb-2">{copy('Description', 'Beschreibung', 'Opis')}</h6>
														<p className="text-muted mb-0">{planAi?.planDescription}</p>
													</div>
												)}
												{planAi?.keyFeatures && planAi.keyFeatures.length > 0 && (
													<div className="mb-3">
														<h6 className="fw-semibold mb-2">{copy('Key Features', 'Hauptmerkmale', 'Ključne značajke')}</h6>
														<ul className="mb-0">
															{planAi.keyFeatures.map((feature, idx) => (
																<li key={idx}>{feature}</li>
															))}
														</ul>
													</div>
												)}
												{plan.styleKey && (
													<div className="mb-3">
														<h6 className="fw-semibold mb-2">{copy('Style', 'Stil', 'Stil')}</h6>
														<span className="badge text-bg-primary text-capitalize">{plan.styleKey}</span>
													</div>
												)}
												{plan.questionnaire?.selectedComponents && plan.questionnaire.selectedComponents.length > 0 && (
													<div className="mb-3">
														<h6 className="fw-semibold mb-2">{copy('Garden Components', 'Gartenkomponenten', 'Komponente vrta')}</h6>
														<div className="d-flex flex-wrap gap-2">
															{plan.questionnaire.selectedComponents.map((componentId) => (
																<span key={componentId} className="badge bg-primary">
																	{getComponentLabel(componentId)}
																</span>
															))}
														</div>
													</div>
												)}
												{(plan.photoBeforeUrl || plan.questionnaire?.photoBeforeUrl || planAi?.photoAfterUrl) && (
													<div className="mt-3">
														<h6 className="fw-semibold mb-3">{copy('Visual Concept', 'Vizualni koncept', 'Vizualni koncept')}</h6>
														<div className="row g-3">
															{(plan.photoBeforeUrl || plan.questionnaire?.photoBeforeUrl) && (
																<div className="col-md-6">
																	<p className="small text-muted mb-1 fw-medium">{copy('Before transformation', 'Prije transformacije', 'Prije transformacije')}</p>
																	<div
																		className="rounded overflow-hidden border bg-light"
																		style={{ minHeight: 200, cursor: 'pointer' }}
																		onClick={() => { setImageModalIndex(0); setImageModalOpen(true); }}
																		role="button"
																		tabIndex={0}
																		onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setImageModalIndex(0); setImageModalOpen(true); } }}
																		aria-label={copy('View before transformation full size', 'Vorher-Bild in voller Größe anzeigen', 'Prikaži prikaz prije transformacije u punoj veličini')}
																	>
																		<img
																			src={getImageUrl(plan.photoBeforeUrl || plan.questionnaire?.photoBeforeUrl) || ''}
																			alt={copy('Before transformation', 'Prije transformacije', 'Prije transformacije')}
																			className="img-fluid w-100"
																			style={{ maxHeight: 280, objectFit: 'cover' }}
																			onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }}
																		/>
																	</div>
																	<p className="small text-muted mt-1 mb-0">{copy('Click to expand', 'Klicken zum Vergrößern', 'Kliknite za proširenje')}</p>
																</div>
															)}
															{planAi?.photoAfterUrl && (
																<div className={plan.photoBeforeUrl || plan.questionnaire?.photoBeforeUrl ? 'col-md-6' : 'col-12'}>
																	<p className="small text-muted mb-1 fw-medium">{copy('After transformation', 'Nakon transformacije', 'Nakon transformacije')}</p>
																	<div
																		className="rounded overflow-hidden border bg-light"
																		style={{ minHeight: 200, cursor: 'pointer' }}
																		onClick={() => {
																			const hasBefore = !!(plan.photoBeforeUrl || plan.questionnaire?.photoBeforeUrl);
																			setImageModalIndex(hasBefore ? 1 : 0);
																			setImageModalOpen(true);
																		}}
																		role="button"
																		tabIndex={0}
																		onKeyDown={(e) => {
																			if (e.key === 'Enter' || e.key === ' ') {
																				e.preventDefault();
																				const hasBefore = !!(plan.photoBeforeUrl || plan.questionnaire?.photoBeforeUrl);
																				setImageModalIndex(hasBefore ? 1 : 0);
																				setImageModalOpen(true);
																			}
																		}}
																		aria-label={copy('View after transformation full size', 'Nachher-Bild in voller Größe anzeigen', 'Prikaži prikaz nakon transformacije u punoj veličini')}
																	>
																		<img
																			src={getImageUrl(planAi.photoAfterUrl) || ''}
																			alt={copy('After transformation', 'Nakon transformacije', 'Nakon transformacije')}
																			className="img-fluid w-100"
																			style={{ maxHeight: 280, objectFit: 'cover' }}
																			onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }}
																		/>
																	</div>
																	<p className="small text-muted mt-1 mb-0">{copy('Click to expand', 'Klicken zum Vergrößern', 'Kliknite za proširenje')}</p>
																</div>
															)}
														</div>
													</div>
												)}
											</div>
										) : (
											<div>
												<p className="text-muted mb-3">
													{project.planId 
														? copy('Plan data is not available. This project may have been created manually or the plan has been removed.', 'Plandaten sind nicht verfügbar. Dieses Projekt wurde möglicherweise manuell erstellt oder der Plan wurde entfernt.', 'Podaci plana nisu dostupni. Projekt je možda ručno kreiran ili je plan uklonjen.')
														: copy('No conceptual plan information available. This project was created without a linked plan. Conceptual information will appear here when a plan is associated with this project.', 'Keine konzeptionellen Planinformationen verfügbar. Dieses Projekt wurde ohne verknüpften Plan erstellt. Die Informationen erscheinen hier, sobald ein Plan mit diesem Projekt verknüpft ist.', 'Nema dostupnih konceptualnih informacija o planu. Projekt je kreiran bez povezanog plana. Informacije će se prikazati kada se plan poveže s projektom.')
													}
												</p>
												{project.tags && project.tags.length > 0 && (
													<div className="mb-3">
														<h6 className="fw-semibold mb-2">{copy('Project Tags', 'Oznake projekta', 'Oznake projekta')}</h6>
														<div className="d-flex flex-wrap gap-2">
															{project.tags.map((tag) => (
																<span key={tag} className="badge text-bg-light text-muted">
																	{tag}
																</span>
															))}
														</div>
													</div>
												)}
											</div>
										)}
									</div>
								)}

								{/* Detailed Tab Content */}
								{detailTab === 'detailed' && (
									<div>
										{plan?.aiGenerated ? (
											<div>
												{planAi?.instructions && planAi.instructions.length > 0 && (
													<div className="mb-3">
														<h6 className="fw-semibold mb-2">{copy('Implementation Instructions', 'Upute za izvedbu', 'Upute za izvedbu')}</h6>
														<ol className="mb-0">
															{planAi.instructions.map((instruction, idx) => (
																<li key={idx} className="mb-2">{instruction}</li>
															))}
														</ol>
													</div>
												)}
												{planAi?.tools && planAi.tools.length > 0 && (
													<div className="mb-3">
														<h6 className="fw-semibold mb-2">{copy('Required Tools', 'Potrebni alati', 'Potrebni alati')}</h6>
														<ul className="mb-0">
															{planAi.tools.map((tool, idx) => {
																if (typeof tool === 'string') {
																	return <li key={idx}>{tool}</li>;
																}
																return (
																	<li key={idx}>
																		<strong>{tool.name}:</strong> {tool.description}
																	</li>
																);
															})}
														</ul>
													</div>
												)}
												{plan.questionnaire && (
													<div>
														<h6 className="fw-semibold mb-2">{copy('Questionnaire Details', 'Fragebogen-Details', 'Detalji upitnika')}</h6>
														<dl className="row mb-0">
															{plan.questionnaire.gardenSize && (
																<>
																	<dt className="col-sm-4">{copy('Garden Size', 'Gartengröße', 'Veličina vrta')}</dt>
																	<dd className="col-sm-8">{plan.questionnaire.gardenSize} m²</dd>
																</>
															)}
															{plan.questionnaire.selectedComponents && plan.questionnaire.selectedComponents.length > 0 && (
																<>
																	<dt className="col-sm-4">{copy('Garden Components', 'Gartenkomponenten', 'Komponente vrta')}</dt>
																	<dd className="col-sm-8">
																		<div className="d-flex flex-wrap gap-2">
																			{plan.questionnaire.selectedComponents.map((componentId) => (
																				<span key={componentId} className="badge bg-primary text-white">
																					{getComponentLabel(componentId)}
																				</span>
																			))}
																		</div>
																	</dd>
																</>
															)}
															{plan.questionnaire.needsWants && (
																<>
																	<dt className="col-sm-4">{copy('Needs & Wants', 'Bedürfnisse & Wünsche', 'Potrebe i želje')}</dt>
																	<dd className="col-sm-8">{plan.questionnaire.needsWants}</dd>
																</>
															)}
															{plan.questionnaire.location && (
																<>
																	<dt className="col-sm-4">{copy('Location', 'Standort', 'Lokacija')}</dt>
																	<dd className="col-sm-8">{plan.questionnaire.location}</dd>
																</>
															)}
															{plan.questionnaire.projectFocus && (
																<>
																	<dt className="col-sm-4">{copy('Project focus', 'Projektfokus', 'Fokus projekta')}</dt>
																	<dd className="col-sm-8">{localizeProjectFocus(plan.questionnaire.projectFocus)}</dd>
																</>
															)}
															{plan.questionnaire.timeline && (
																<>
																	<dt className="col-sm-4">{copy('Timeline', 'Zeitplan', 'Vremenski plan')}</dt>
																	<dd className="col-sm-8">{plan.questionnaire.timeline}</dd>
																</>
															)}
														</dl>
													</div>
												)}
											</div>
										) : (
											<div>
												<p className="text-muted mb-3">
													{project.planId 
														? copy('Detailed plan information is not available. This project may have been created manually or the plan has been removed.', 'Detaillierte Planinformationen sind nicht verfügbar. Dieses Projekt wurde möglicherweise manuell erstellt oder der Plan wurde entfernt.', 'Detaljne informacije plana nisu dostupne. Projekt je možda ručno kreiran ili je plan uklonjen.')
														: copy('No detailed plan information available. This project was created without a linked plan. Detailed instructions and questionnaire data will appear here when a plan is associated with this project.', 'Keine detaillierten Planinformationen verfügbar. Dieses Projekt wurde ohne verknüpften Plan erstellt. Detaillierte Anweisungen und Fragebogendaten erscheinen hier, sobald ein Plan mit diesem Projekt verknüpft ist.', 'Nema dostupnih detaljnih informacija plana. Projekt je kreiran bez povezanog plana. Upute i podaci upitnika prikazat će se kada se plan poveže s projektom.')
													}
												</p>
												<div className="mb-3">
													<h6 className="fw-semibold mb-2">{copy('Project Information', 'Informacije o projektu', 'Informacije o projektu')}</h6>
													<dl className="row mb-0">
														<dt className="col-sm-4">{copy('Client', 'Kunde', 'Klijent')}</dt>
														<dd className="col-sm-8">{project.client || '-'}</dd>
														<dt className="col-sm-4">{copy('Budget', 'Budget', 'Budžet')}</dt>
														<dd className="col-sm-8">{project.budget ? project.budget.toLocaleString() : '0'} €</dd>
														{project.dueDate && (
															<>
																<dt className="col-sm-4">{copy('Due Date', 'Fälligkeitsdatum', 'Datum dospijeća')}</dt>
																<dd className="col-sm-8">{new Date(project.dueDate).toLocaleDateString('en-GB')}</dd>
															</>
														)}
													</dl>
												</div>
											</div>
										)}
									</div>
								)}

								{/* Project Plan Tab Content */}
								{detailTab === 'project-plan' && (
									<div>
										<div className="card mb-4 border-success border-opacity-25">
											<div className="card-header bg-transparent border-success border-opacity-25 py-2">
												<div className="d-flex flex-wrap justify-content-between align-items-center gap-2">
													<h6 className="mb-0 fw-semibold">{copy('Schedule preview', 'Zeitplan-Vorschau', 'Pregled rasporeda')}</h6>
													<button
														type="button"
														className="btn btn-outline-success btn-sm"
														onClick={() => {
															setActiveTab('timeline');
															setSearchParams({});
														}}
													>
														{copy('Full timeline', 'Puni vremenski plan', 'Puni vremenski plan')}
													</button>
												</div>
											</div>
											<div className="card-body pt-2">
												{timelineGanttTasks.length > 0 ? (
													<GanttChart
														preview
														tasks={timelineGanttTasks}
														companyCountryCode={companyCountryCode}
														gardeners={assignedGardeners.map((g) => ({
															id: String(g.id),
															name: g.name,
															role: g.role === 'gardener' ? 'gardener' : g.role === 'company' ? 'lead' : 'gardener',
															organizationId: g.company_name || undefined
														}))}
														machines={availableMachines}
														editable={false}
														onTaskClick={() => {
															setActiveTab('timeline');
															setSearchParams({});
														}}
													/>
												) : (
													<p className="text-muted small mb-0">
														{copy('No scheduled tasks yet. Open the', 'Noch keine Aufgaben geplant. Öffnen Sie den', 'Još nema zakazanih zadataka. Otvorite karticu')} <strong>{copy('Timeline', 'Zeitplan', 'Vremenski plan')}</strong> {copy('tab to generate or manage the schedule.', ', um den Zeitplan zu erstellen oder zu verwalten.', 'za generiranje ili upravljanje rasporedom.')}
													</p>
												)}
											</div>
										</div>
										{pricingBreakdown ? (
											<div>
												<div className="mb-3">
													<h6 className="fw-semibold mb-2">{copy('Pricing Breakdown', 'Kostenaufschlüsselung', 'Raščlamba cijena')}</h6>
													<div className="row mb-2">
														<div className="col-md-4">
															<strong>{copy('Minimum:', 'Minimum:', 'Minimum:')}</strong> {pricingBreakdown.min.toLocaleString()} €
														</div>
														<div className="col-md-4">
															<strong>{copy('Mid-range:', 'Srednji raspon:', 'Srednji raspon:')}</strong> {pricingBreakdown.mid.toLocaleString()} €
														</div>
														<div className="col-md-4">
															<strong>{copy('Maximum:', 'Maksimum:', 'Maksimum:')}</strong> {pricingBreakdown.max.toLocaleString()} €
														</div>
													</div>
												</div>
												{pricingBreakdown.materials && pricingBreakdown.materials.length > 0 && (
													<div className="mb-3">
														<h6 className="fw-semibold mb-2">{copy('Materials', 'Materialien', 'Materijali')}</h6>
														<div className="table-responsive">
															<table className="table table-sm">
																<thead>
																	<tr>
																		<th>{copy('Name', 'Name', 'Naziv')}</th>
																		<th>{copy('Quantity', 'Menge', 'Količina')}</th>
																		<th>{copy('Unit Price', 'Stückpreis', 'Jedinična cijena')}</th>
																		<th>{copy('Total', 'Ukupno', 'Ukupno')}</th>
																	</tr>
																</thead>
																<tbody>
																	{pricingBreakdown.materials.map((material, idx) => (
																		<tr key={idx}>
																			<td>{material.name}</td>
																			<td>{material.quantity} {material.unit}</td>
																			<td>{material.unitPrice.toLocaleString()} €</td>
																			<td>{material.total.toLocaleString()} €</td>
																		</tr>
																	))}
																</tbody>
															</table>
														</div>
													</div>
												)}
												{pricingBreakdown.labor && pricingBreakdown.labor.length > 0 && (
													<div className="mb-3">
														<h6 className="fw-semibold mb-2">{copy('Labor', 'Rad', 'Rad')}</h6>
														<div className="table-responsive">
															<table className="table table-sm">
																<thead>
																	<tr>
																		<th>{copy('Name', 'Name', 'Naziv')}</th>
																		<th>{copy('Hours', 'Stunden', 'Sati')}</th>
																		<th>{copy('Rate/Hour', 'Cijena/sat', 'Cijena/sat')}</th>
																		<th>{copy('Total', 'Ukupno', 'Ukupno')}</th>
																	</tr>
																</thead>
																<tbody>
																	{pricingBreakdown.labor.map((labor, idx) => (
																		<tr key={idx}>
																			<td>{labor.name}</td>
																			<td>{labor.hours}</td>
																			<td>{labor.ratePerHour.toLocaleString()} €</td>
																			<td>{labor.total.toLocaleString()} €</td>
																		</tr>
																	))}
																</tbody>
															</table>
														</div>
													</div>
												)}
												{offer && (
													<div className="mt-3">
														<h6 className="fw-semibold mb-2">{copy('Offer Details', 'Angebotsdetails', 'Detalji ponude')}</h6>
														<dl className="row mb-0">
															<dt className="col-sm-4">{copy('Pricing Bands', 'Cjenovni rasponi', 'Cjenovni rasponi')}</dt>
															<dd className="col-sm-8">
																{copy('Min', 'Min', 'Min')}: {offer.bands.min.toLocaleString()} € | 
																{copy('Mid', 'Srednje', 'Srednje')}: {offer.bands.mid.toLocaleString()} € | 
																{copy('Max', 'Maks', 'Maks')}: {offer.bands.max.toLocaleString()} €
															</dd>
															{offer.timelineEstimate && (
																<>
																	<dt className="col-sm-4">{copy('Timeline Estimate', 'Procjena vremenskog plana', 'Procjena vremenskog plana')}</dt>
																	<dd className="col-sm-8">{offer.timelineEstimate}</dd>
																</>
															)}
															{offer.message && (
																<>
																	<dt className="col-sm-4">{copy('Notes', 'Notizen', 'Bilješke')}</dt>
																	<dd className="col-sm-8">{offer.message}</dd>
																</>
															)}
														</dl>
													</div>
												)}
											</div>
										) : (
											<div>
												<p className="text-muted mb-3">
													{project.planId 
														? copy('Project plan information is not available. This project may have been created manually or the plan has been removed.', 'Projektplan-Informationen sind nicht verfügbar. Dieses Projekt wurde möglicherweise manuell erstellt oder der Plan wurde entfernt.', 'Informacije plana projekta nisu dostupne. Projekt je možda ručno kreiran ili je plan uklonjen.')
														: copy('No project plan information available. This project was created without a linked plan. Pricing breakdown and offer details will appear here when a plan is associated with this project.', 'Keine Projektplan-Informationen verfügbar. Dieses Projekt wurde ohne verknüpften Plan erstellt. Kostenaufschlüsselung und Angebotsdetails erscheinen hier, sobald ein Plan zugeordnet ist.', 'Nema dostupnih informacija plana projekta. Projekt je kreiran bez povezanog plana. Raščlamba cijena i detalji ponude pojavit će se kada se plan poveže s projektom.')
													}
												</p>
												{offer ? (
													<div className="mb-3">
														<h6 className="fw-semibold mb-2">{copy('Offer Information', 'Informacije o ponudi', 'Informacije o ponudi')}</h6>
														<dl className="row mb-0">
															{offer.bands && (
																<>
																	<dt className="col-sm-4">{copy('Pricing Bands', 'Cjenovni rasponi', 'Cjenovni rasponi')}</dt>
																	<dd className="col-sm-8">
																		{copy('Min', 'Min', 'Min')}: {offer.bands.min.toLocaleString()} € | 
																		{copy('Mid', 'Srednje', 'Srednje')}: {offer.bands.mid.toLocaleString()} € | 
																		{copy('Max', 'Maks', 'Maks')}: {offer.bands.max.toLocaleString()} €
																	</dd>
																</>
															)}
															{offer.timelineEstimate && (
																<>
																	<dt className="col-sm-4">{copy('Timeline Estimate', 'Procjena vremenskog plana', 'Procjena vremenskog plana')}</dt>
																	<dd className="col-sm-8">{offer.timelineEstimate}</dd>
																</>
															)}
														</dl>
													</div>
												) : (
													<div className="mb-3">
														<h6 className="fw-semibold mb-2">{copy('Project Budget', 'Projektbudget', 'Budžet projekta')}</h6>
														<p className="mb-0">{project.budget ? project.budget.toLocaleString() : '0'} €</p>
													</div>
												)}
											</div>
										)}
									</div>
								)}
							</div>
						</div>
					</div>
				</div>
			)}

			{activeTab === 'timeline' && (
				<>
					{/* Re-plan history - company/admin only */}
					{(user?.role === 'company' || user?.role === 'admin') && (
						<div className="card mb-3">
							<div className="card-body">
								<h5 className="card-title mb-2">{copy('Re-plan history', 'Povijest ponovnog planiranja', 'Povijest ponovnog planiranja')}</h5>
								{isLoadingReplans ? (
									<p className="text-muted small mb-0">{copy('Loading...', 'Wird geladen...', 'Učitavanje...')}</p>
								) : replans.length === 0 ? (
									<p className="text-muted small mb-0">{copy('No re-plan versions yet.', 'Noch keine Neuplanungs-Versionen.', 'Još nema verzija ponovnog planiranja.')}</p>
								) : (
									<ul className="list-unstyled mb-0">
										{replans.map((r) => (
											<li key={r.id} className="d-flex align-items-center justify-content-between py-2 border-bottom border-light">
												<div className="small">
													<span className={`badge ${r.status === 'applied' ? 'text-bg-success' : r.status === 'discarded' ? 'text-bg-secondary' : 'text-bg-light text-dark'}`}>
														{r.status}
													</span>
													{r.applied_at && (
														<span className="ms-2 text-muted">
															{copy('Applied', 'Primijenjeno', 'Primijenjeno')} {new Date(r.applied_at).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })}
														</span>
													)}
													{!r.applied_at && r.created_at && (
														<span className="ms-2 text-muted">{copy('Created', 'Erstellt', 'Kreirano')} {new Date(r.created_at).toLocaleDateString('en-GB')}</span>
													)}
													{r.applied_task_ids?.length > 0 && (
														<span className="ms-2 text-muted">({r.applied_task_ids.length} {copy('new tasks', 'novi zadaci', 'novi zadaci')})</span>
													)}
												</div>
												{r.status === 'applied' && (
													<button
														type="button"
														className="btn btn-outline-danger btn-sm"
														onClick={() => handleRevertReplan(r.id)}
														disabled={isReverting}
													>
														{isReverting ? copy('Reverting...', 'Wird zurückgesetzt...', 'Vraćanje...') : copy('Undo', 'Rückgängig', 'Poništi')}
													</button>
												)}
											</li>
										))}
									</ul>
								)}
							</div>
						</div>
					)}
					<div className="card">
					<div className="card-body">
						<div className="d-flex justify-content-between align-items-center mb-3">
							<h5 className="card-title mb-0">{copy('Project Timeline', 'Projektzeitplan', 'Vremenski plan projekta')}</h5>
							<div className="d-flex gap-2">
								{(user?.role === 'company' || user?.role === 'gardener') && (
									<button
										className="btn btn-success btn-sm"
										onClick={() => setShowCreateTaskModal(true)}
									>
										<i className="bi bi-plus-circle me-1"></i>
										{copy('Add Task', 'Aufgabe hinzufügen', 'Dodaj zadatak')}
									</button>
								)}
								{(!project.ganttData || project.ganttData.length === 0) && (user?.role === 'company' || user?.role === 'gardener') && (
									<button
										className="btn btn-outline-success btn-sm"
										onClick={handleGenerateTimeline}
									>
										<i className="bi bi-calendar-plus me-1"></i>
										{copy('Generate Timeline', 'Generiraj vremenski plan', 'Generiraj vremenski plan')}
									</button>
								)}
							</div>
						</div>
						{timelineGanttTasks.length > 0 ? (
							<>
								<GanttChart
									tasks={timelineGanttTasks}
									companyCountryCode={companyCountryCode}
									gardeners={assignedGardeners.map(g => ({
										id: String(g.id),
										name: g.name,
										role: g.role === 'gardener' ? 'gardener' : g.role === 'company' ? 'lead' : 'gardener',
										organizationId: g.company_name || undefined
									}))}
									machines={availableMachines}
									editable={user?.role === 'company' || user?.role === 'gardener'}
									onTaskClick={handleTaskClick}
									onTaskUpdate={handleTaskUpdate}
								/>
								{editingTask && (
									<TaskEditModal
										task={editingTask}
										gardeners={companyScopedGardeners.map(g => ({
											id: String(g.id),
											name: g.name,
											role: g.role === 'gardener' ? 'gardener' : g.role === 'company' ? 'lead' : 'gardener',
											organizationId: g.company_name || undefined
										}))}
										organizationId={user?.organizationId || 'org1'}
										onSave={handleTaskUpdate}
										onClose={() => setEditingTask(null)}
									/>
								)}
							</>
						) : (
							<div className="alert alert-info">
								<div className="d-flex justify-content-between align-items-center">
									<div>
										<strong>{copy('No timeline data available.', 'Podaci vremenskog plana nisu dostupni.', 'Podaci vremenskog plana nisu dostupni.')}</strong>
										<p className="mb-0 mt-2">{copy('Generate a mock timeline based on the project start dates, or wait for a detailed timeline when available.', 'Erstellen Sie einen vorläufigen Zeitplan anhand der Projektstartdaten oder warten Sie auf einen detaillierten Zeitplan.', 'Generirajte probni vremenski plan prema datumima početka projekta ili pričekajte detaljan vremenski plan kada bude dostupan.')}</p>
										{plan?.questionnaire?.projectStartDate && (
											<p className="mb-0 mt-1 small">
												{copy('Seeker preferred start:', 'Gewünschter Start des Suchenden:', 'Željeni početak vlasnika:')} <strong>{new Date(plan.questionnaire.projectStartDate).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}</strong>
											</p>
										)}
										{offer?.projectStartDate && (
											<p className="mb-0 mt-1 small">
												{copy('Company possible start:', 'Möglicher Start der Firma:', 'Mogući početak tvrtke:')} <strong>{new Date(offer.projectStartDate).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}</strong>
											</p>
										)}
									</div>
									{(user?.role === 'company' || user?.role === 'gardener') && (
										<button
											className="btn btn-success"
											onClick={handleGenerateTimeline}
										>
											<i className="bi bi-calendar-plus me-1"></i>
											{copy('Generate Timeline', 'Generiraj vremenski plan', 'Generiraj vremenski plan')}
										</button>
									)}
								</div>
							</div>
						)}
					</div>
				</div>
		</>
			)}

			{activeTab === 'team' && (
				<div className="card">
					<div className="card-body">
						<h5 className="card-title mb-3">{copy('Team Members', 'Teammitglieder', 'Članovi tima')}</h5>
						
						{/* Add Gardener */}
						<div className="mb-4">
							<div className="row g-2">
								<div className="col-md-6">
									<select
										className="form-select"
										value={selectedGardenerId}
										onChange={(e) => setSelectedGardenerId(e.target.value)}
										disabled={isLoadingGardeners}
									>
										<option value="">{isLoadingGardeners ? copy('Loading gardeners...', 'Gärtner werden geladen...', 'Učitavanje vrtlara...') : companyScopedGardeners.length === 0 ? copy('No gardeners available', 'Keine Gärtner verfügbar', 'Nema dostupnih vrtlara') : copy('Select a gardener to add...', 'Wählen Sie einen Gärtner zum Hinzufügen...', 'Odaberite vrtlara za dodavanje...')}</option>
										{companyScopedGardeners
											.filter(g => {
												const gardenerIdStr = String(g.id);
												return !project.gardenerIds?.some(id => String(id) === gardenerIdStr);
											})
											.map((gardener) => (
													<option key={gardener.id} value={String(gardener.id)}>
														{gardener.name} {gardener.company_name ? `(${gardener.company_name})` : gardener.role ? `(${gardener.role})` : ''}
													</option>
												))}
									</select>
								</div>
								<div className="col-md-6">
									<button
										className="btn btn-success"
										onClick={handleAddGardener}
										disabled={!selectedGardenerId}
									>
										{copy('Add Gardener', 'Gärtner hinzufügen', 'Dodaj vrtlara')}
									</button>
								</div>
							</div>
						</div>

						{/* Assigned Gardeners */}
						{assignedGardeners.length > 0 ? (
							<div className="table-responsive">
								<table className="table">
									<thead>
										<tr>
											<th>{copy('Name', 'Name', 'Ime')}</th>
											<th>{copy('Role', 'Rolle', 'Uloga')}</th>
											<th>{copy('Skills', 'Fähigkeiten', 'Vještine')}</th>
											<th>{copy('Actions', 'Aktionen', 'Radnje')}</th>
										</tr>
									</thead>
									<tbody>
										{assignedGardeners.map((gardener) => (
											<tr key={gardener.id}>
												<td>{gardener.name}</td>
												<td>{gardener.role || gardener.company_name || '-'}</td>
												<td>
													{Array.isArray(gardener.skills) && gardener.skills.length > 0
														? gardener.skills.map(skill => getSkillLabel(skill, language)).join(', ')
														: '-'}
												</td>
												<td>
													<button
														className="btn btn-sm btn-outline-danger"
														onClick={() => handleRemoveGardener(String(gardener.id))}
													>
														{copy('Remove', 'Entfernen', 'Ukloni')}
													</button>
												</td>
											</tr>
										))}
									</tbody>
								</table>
							</div>
						) : (
							<div className="alert alert-info">
								{copy('No gardeners assigned yet. Add gardeners to start working on this project.', 'Noch keine Gärtner zugewiesen. Fügen Sie Gärtner hinzu, um mit diesem Projekt zu starten.', 'Još nema dodijeljenih vrtlara. Dodajte vrtlare da započnete rad na ovom projektu.')}
							</div>
						)}
					</div>
				</div>
			)}

			{activeTab === 'messages' && projectId && user && (() => {
				const projectIdNum = parseInt(projectId, 10);
				const userIdNum = getMessagingActingUserId(user, viewAsTarget);
				if (isNaN(projectIdNum) || userIdNum == null || isNaN(userIdNum)) return null;
				return (
					<div className="card">
						<div className="card-body">
							<ConversationThread
								variant="project"
								projectId={projectIdNum}
								currentUserId={userIdNum}
								title={copy('Conversation with client', 'Razgovor s klijentom', 'Razgovor s klijentom')}
							/>
						</div>
					</div>
				);
			})()}

			{/* Client Inputs Tab */}
			{activeTab === 'questionnaire' && plan && (
				<div className="card">
					<div className="card-body">
						<h5 className="card-title text-success mb-3">{copy('Client Inputs', 'Kundeneingaben', 'Unos klijenta')}</h5>
						{plan.questionnaire ? (
							<>
								<div className="row g-3 mb-4">
									{plan.questionnaire.gardenSize > 0 && (
										<div className="col-12 col-md-4">
											<div className="text-muted small mb-1">{copy('Garden size', 'Gartengröße', 'Veličina vrta')}</div>
											<div className="fw-semibold">{plan.questionnaire.gardenSize} m²</div>
										</div>
									)}

									{plan.questionnaire.gardenShape && (
										<div className="col-12 col-md-4">
											<div className="text-muted small mb-1">{copy('Garden shape', 'Oblik vrta', 'Oblik vrta')}</div>
											<div className="fw-semibold">{plan.questionnaire.gardenShape}</div>
										</div>
									)}

									{plan.questionnaire.location && (
										<div className="col-12 col-md-4">
											<div className="text-muted small mb-1">{copy('Location', 'Standort', 'Lokacija')}</div>
											<div className="fw-semibold">{plan.questionnaire.location}</div>
										</div>
									)}
									{plan.questionnaire.projectFocus && (
										<div className="col-12 col-md-4">
											<div className="text-muted small mb-1">{copy('Project focus', 'Projektfokus', 'Fokus projekta')}</div>
											<div className="fw-semibold">{localizeProjectFocus(plan.questionnaire.projectFocus)}</div>
										</div>
									)}

									{plan.questionnaire.selectedComponents && plan.questionnaire.selectedComponents.length > 0 && (
										<div className="col-12">
											<div className="text-muted small mb-1">{copy('Garden components', 'Gartenkomponenten', 'Komponente vrta')}</div>
											<div className="d-flex flex-wrap gap-2">
												{plan.questionnaire.selectedComponents.map((componentId) => (
													<span key={componentId} className="badge bg-success text-white">
														<i className="bi bi-check-circle me-1" />
														{getComponentLabel(componentId)}
													</span>
												))}
											</div>
										</div>
									)}

									{plan.questionnaire.needsWants && plan.questionnaire.needsWants.trim().length > 0 && (
										<div className="col-12">
											<div className="text-muted small mb-1">{copy('Needs & wants', 'Bedürfnisse & Wünsche', 'Potrebe i želje')}</div>
											<div className="text-break">{plan.questionnaire.needsWants}</div>
										</div>
									)}

									{plan.questionnaire.maintenanceLevel && (
										<div className="col-12 col-md-4">
											<div className="text-muted small mb-1">{copy('Maintenance level', 'Pflegeaufwand', 'Razina održavanja')}</div>
											<div className="fw-semibold text-capitalize">{plan.questionnaire.maintenanceLevel}</div>
										</div>
									)}

									{plan.questionnaire.plantDensityPreference && (
										<div className="col-12 col-md-4">
											<div className="text-muted small mb-1">{copy('Plant density', 'Pflanzdichte', 'Gustoća sadnje')}</div>
											<div className="fw-semibold text-capitalize">{plan.questionnaire.plantDensityPreference}</div>
										</div>
									)}

									{plan.questionnaire.gardenUses && plan.questionnaire.gardenUses.length > 0 && (
										<div className="col-12">
											<div className="text-muted small mb-1">{copy('How you’ll use the garden', 'Wie Sie den Garten nutzen', 'Kako ćete koristiti vrt')}</div>
											<div className="d-flex flex-wrap gap-2">
												{plan.questionnaire.gardenUses.map((useKey) => {
													const labelMap: Record<string, string> = {
														relaxing: copy('Relaxing / quiet time', 'Entspannung / Ruhezeit', 'Opuštanje / mir'),
														growing_food: copy('Growing food', 'Uzgoj hrane', 'Uzgoj hrane'),
														kids_pets: copy('Kids & pets', 'Kinder & Haustiere', 'Djeca i kućni ljubimci'),
														hosting: copy('Hosting', 'Gäste empfangen', 'Druženje'),
														dining_outdoors: copy('Outdoor dining', 'Blagovanje na otvorenom', 'Blagovanje na otvorenom')
													};
													return (
														<span key={useKey} className="badge bg-secondary text-white">
															{labelMap[useKey] ?? useKey}
														</span>
													);
												})}
											</div>
										</div>
									)}

									{plan.questionnaire.hostingMaxPeople != null && plan.questionnaire.hostingMaxPeople > 0 && (
										<div className="col-12 col-md-4">
											<div className="text-muted small mb-1">{copy('Hosting (max people)', 'Gäste empfangen (max. Personen)', 'Druženje (maks. osoba)')}</div>
											<div className="fw-semibold">{plan.questionnaire.hostingMaxPeople}</div>
										</div>
									)}

									{plan.questionnaire.childrenPets && plan.questionnaire.childrenPets !== 'none' && (
										<div className="col-12 col-md-4">
											<div className="text-muted small mb-1">{copy('Kids/pets priority', 'Prioritet djece/ljubimaca', 'Prioritet djece/ljubimaca')}</div>
											<div className="fw-semibold text-capitalize">{plan.questionnaire.childrenPets}</div>
										</div>
									)}

									{plan.questionnaire.kidsCount != null && plan.questionnaire.kidsCount > 0 && (
										<div className="col-12 col-md-4">
											<div className="text-muted small mb-1">{copy('Kids count', 'Broj djece', 'Broj djece')}</div>
											<div className="fw-semibold">{plan.questionnaire.kidsCount}</div>
										</div>
									)}

									{plan.questionnaire.kidsAges && plan.questionnaire.kidsAges.trim().length > 0 && (
										<div className="col-12 col-md-4">
											<div className="text-muted small mb-1">{copy('Kids ages', 'Dob djece', 'Dob djece')}</div>
											<div className="fw-semibold">{plan.questionnaire.kidsAges}</div>
										</div>
									)}

									{plan.questionnaire.petsDescription && plan.questionnaire.petsDescription.trim().length > 0 && (
										<div className="col-12">
											<div className="text-muted small mb-1">{copy('Pets note', 'Napomena o ljubimcima', 'Napomena o ljubimcima')}</div>
											<div className="text-break">{plan.questionnaire.petsDescription}</div>
										</div>
									)}

									{plan.questionnaire.budgetRange && (
										<div className="col-12">
											<div className="text-muted small mb-1">{copy('Budget range', 'Budgetspanne', 'Raspon budžeta')}</div>
											<div className="fw-semibold">
												€{plan.questionnaire.budgetRange.min.toLocaleString()} - €{plan.questionnaire.budgetRange.max.toLocaleString()}
											</div>
											{plan.questionnaire.budgetPreference != null && (
												<div className="text-muted small mt-1">
													{copy('Budget preference:', 'Budget-Präferenz:', 'Preferencija budžeta:')} {plan.questionnaire.budgetPreference}/100
												</div>
											)}
										</div>
									)}

									{plan.questionnaire.timeline && plan.questionnaire.timeline.trim().length > 0 && (
										<div className="col-12">
											<div className="text-muted small mb-1">{copy('Timeline', 'Zeitplan', 'Vremenski plan')}</div>
											<div className="fw-semibold">{plan.questionnaire.timeline}</div>
										</div>
									)}

									{plan.questionnaire.projectStartDate && (
										<div className="col-12">
											<div className="text-muted small mb-1">{copy('Preferred start date', 'Bevorzugtes Startdatum', 'Željeni datum početka')}</div>
											<div className="fw-semibold">
												{new Date(plan.questionnaire.projectStartDate).toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' })}
											</div>
										</div>
									)}
								</div>

								<div className="mt-3">
									<div className="text-muted small mb-2">{copy('Smart question responses', 'Antworten auf intelligente Fragen', 'Odgovori na pametna pitanja')}</div>
									{(() => {
										const q = plan.questionnaire!;
										const aiAnswers = (q.aiAnswers ?? {}) as Record<string, any>;
										const aiQuestionComments = (q.aiQuestionComments ?? {}) as Record<string, string>;
										const suggested = q.aiAnalysis?.suggestedQuestions ?? [];

										const answerKeys = Object.keys(aiAnswers).filter((k) => /^ai_q_\d+$/.test(k));
										const validAnswerKeys = answerKeys.filter((k) => {
											const v = aiAnswers[k];
											if (v == null) return false;
											if (typeof v === 'string') return v.trim().length > 0;
											if (Array.isArray(v)) return v.some((x) => String(x ?? '').trim().length > 0);
											return true;
										});

										const indices = new Set<number>();
										for (const k of validAnswerKeys) {
											const idx = Number(k.match(/^ai_q_(\d+)$/)?.[1]);
											if (!Number.isNaN(idx)) indices.add(idx);
										}
										for (const k of Object.keys(aiQuestionComments)) {
											const idx = Number(k);
											if (!Number.isNaN(idx) && aiQuestionComments[k]?.trim().length) indices.add(idx);
										}

										const sorted = Array.from(indices).sort((a, b) => a - b);
										if (sorted.length === 0) {
											return <p className="text-muted mb-0">No Smart Questions were answered.</p>;
										}

										const formatAnswer = (val: any): string => {
											if (val == null) return '';
											if (Array.isArray(val)) return val.filter((x) => String(x ?? '').trim().length > 0).map(String).join(', ');
											if (typeof val === 'boolean') return val ? 'Yes' : 'No';
											const str = String(val);
											if (str === 'yes') return 'Yes';
											if (str === 'no') return 'No';
											return str;
										};

										return (
											<details>
												<summary style={{ cursor: 'pointer', fontWeight: 600 }}>
													Show answered Smart Questions ({sorted.length})
												</summary>
												<div className="mt-3">
													{sorted.map((idx) => {
														const answerKey = `ai_q_${idx}`;
														const answerVal = aiAnswers[answerKey];
														const commentVal = aiQuestionComments[String(idx)];
														const qText = suggested?.[idx]?.question;
														const title = qText?.trim().length ? qText : 'Smart question';
														const answerText = formatAnswer(answerVal);
														const hasAnswer = answerText.trim().length > 0;
														const hasComment = Boolean(commentVal && commentVal.trim().length);

														if (!hasAnswer && !hasComment) return null;

														return (
															<div key={idx} className="mb-3 pb-3 border-bottom">
																<div className="fw-semibold mb-1">{title}</div>
																{hasAnswer && <div className="small text-muted">Answer: {answerText}</div>}
																{hasComment && (
																	<div className="small mt-1">
																		<i className="bi bi-chat-left-text me-1" />
																		<strong>Your note:</strong> <span className="text-break">{commentVal}</span>
																	</div>
																)}
															</div>
														);
													})}
												</div>
											</details>
										);
									})()}
								</div>
							</>
						) : (
							<p className="text-muted mb-0">Client inputs are not available for this project yet.</p>
						)}
					</div>
				</div>
			)}

			{/* Expandable image modal (before/after) */}
			{plan && imageModalOpen && (() => {
				const beforeUrl = getImageUrl(plan.photoBeforeUrl || plan.questionnaire?.photoBeforeUrl);
				const afterUrl = planAi?.photoAfterUrl ? getImageUrl(planAi.photoAfterUrl) : undefined;
				const images: Array<{ url: string; alt: string; label: string }> = [];
				if (beforeUrl) images.push({ url: beforeUrl, alt: 'Before transformation', label: 'Before transformation' });
				if (afterUrl) images.push({ url: afterUrl, alt: 'After transformation', label: 'After transformation' });
				if (images.length === 0) return null;
				return (
					<ImageModal
						isOpen={imageModalOpen}
						onClose={() => setImageModalOpen(false)}
						images={images}
						initialIndex={imageModalIndex}
					/>
				);
			})()}

			{/* Task Create Modal */}
			{showCreateTaskModal && projectId && (
				<TaskCreateModal
					projectId={projectId}
					gardeners={availableGardeners}
					onClose={() => setShowCreateTaskModal(false)}
					onSuccess={async () => {
						// Reload project to get updated tasks
						const updatedProject = await getProjectById(projectId);
						if (updatedProject) {
							setProject(updatedProject);
						}
						setShowCreateTaskModal(false);
					}}
				/>
			)}
		</div>
	);
}

